import React, { memo, useState } from 'react';
import { Button, Tooltip } from '@heroui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion, faUpload } from '@fortawesome/free-solid-svg-icons';
import Papa from 'papaparse';
import { map } from 'lodash';
import Label from '../Label';
import { showErrorAlert, showSuccessAlert } from '../../../../services/globalAlertService';

export interface IFileUploadProps {
	uploadData: (imoNumbers: string[]) => void;
}

const FileUpload: React.FC<IFileUploadProps> = memo(({ uploadData }) => {
	const [fileName, setFileName] = useState<string>('');
	const [error, setError] = useState<string>('');

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			const fileExtension = file.name.split('.').pop()?.toLowerCase();

			if (fileExtension !== 'csv') {
				setError('Only CSV files are allowed.');
				showErrorAlert('Only CSV files are allowed.');
				return;
			}

			Papa.parse(file, {
				delimitersToGuess: [',', '\t', '|', ';'],
				header: true,
				complete: (results) => {
					const data = results.data as string[];
					const objectKeysCount = Object.keys(data[0]).length;

					if (data.length === 0 || objectKeysCount !== 1 || Object.keys(data[0])[0] !== 'ImoNumber') {
						setError('CSV must have a single column with the header "ImoNumber".');
						showErrorAlert('CSV must have a single column with the header "ImoNumber".');
					} else {
						const imoNumbers = map(data, 'ImoNumber');
						uploadData(imoNumbers);
						setFileName(file.name);
						setError('');
						showSuccessAlert(`${imoNumbers.length} vessels are applied to filter.`);
					}
				},
				error: (error) => {
					showErrorAlert(error.message);
					setError('Error parsing CSV file.');
				},
			});
		}
	};

	const fileInfo = error ? 'Wrong file format' : fileName || 'No file chosen';

	const renderHelpIcon = () => {
		return (
			<Tooltip
				placement="top"
				showArrow={true}
				className="text-xs font-normal rounded w-64 p-1 m-1"
				content="The CSV file must have a single column with the header 'ImoNumber'."
			>
				<FontAwesomeIcon icon={faCircleQuestion} />
			</Tooltip>
		);
	};

	return (
		<div className="flex flex-row items-center">
			<input type="file" accept=".csv" multiple={false} onChange={handleFileChange} id="file-upload" className="hidden" />
			<Label title="Upload Portfolio List" textSize="xs" labelClassName="text-nowrap mr-2" />
			<label htmlFor="file-upload">
				<Button
					as="span"
					size="sm"
					className="bg-msi-chart-orange text-white rounded-l rounded-r-none"
					startContent={<FontAwesomeIcon icon={faUpload} />}
					endContent={renderHelpIcon()}
				>
					Choose CSV File
				</Button>
			</label>
			<div
				className={`h-8 w-36 truncate text-xs text-center border p-1.5 border-msi-chart-orange rounded-r rounded-l-none ${error ? 'text-danger' : ''}`}
			>
				{fileInfo}
			</div>
		</div>
	);
});

export default FileUpload;
